import { createApi } from '@reduxjs/toolkit/query/react';
import { waPlatformQuery } from './baseQueries';

export interface ApiResponse {
  Result: 'Success' | 'Error';
  Message?: string;
}

export interface PasswordViewModel {
  Username: string;
  Password: string;
  ConfirmPassword: string;
}

export interface SecurityAnswer {
  SecurityQuestionId: number;
  Answer: string;
}

export interface ConfirmAccountRequest {
  Token: string;
  FirstName?: string;
  LastName?: string;
  PasswordViewModel: PasswordViewModel;
  Answers: SecurityAnswer[];
}

export interface ConfirmAccountJsonResponse {
  error?: string;

  Model?: any;
  TotalSteps?: number;
  RoleCount?: number;
  DefaultRole?: string;
  Roles?: string[];

  Advisor?: string;
  Advisors?: string;
  AdvisorGroupName?: string;

  ForceTwoFactor?: boolean;

  RoleLatestTermsHtml?: string;
  RoleLatestTermId?: number;

  PasswordComplexity?: string;
  UserName?: string;

  IsInvitationExpired?: boolean;
}

export interface SaveAdvisorControlOnboardingRequest {
  userName: string;
  token: string;
  fullControl: boolean;
}

export interface SaveTermsRequest {
  userName: string;
  roleName: string;
  termId: number;
  stepNo: number;
}

export interface SaveTermsResponse extends ApiResponse {
  TermId?: number;
  Terms?: string;
  CurrentStepNo?: number;
}

export interface SetDefaultLandingRequest {
  userName: string;
  roleName: string;
  saveDefaultLanding: boolean;
  stepNo: number;
}

export interface SetDefaultLandingResponse extends ApiResponse {
  Terms?: string;
  TermId?: number;
  CurrentStepNo?: number;
  TermsCount?: number;
}

function normalizeApiResponse<T extends ApiResponse>(response: T): T {
  if (response.Result === 'Error') {
    throw new Error(response.Message);
  }

  return response;
}

function toFormUrlEncoded(
  data: Record<string, any>,
  prefix = '',
): URLSearchParams {
  const params = new URLSearchParams();

  Object.entries(data).forEach(([key, value]) => {
    const formKey = prefix ? `${prefix}.${key}` : key;

    if (value === undefined || value === null) return;

    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (typeof item === 'object') {
          Object.entries(item).forEach(([k, v]) => {
            params.append(`${formKey}[${index}].${k}`, String(v));
          });
        } else {
          params.append(`${formKey}[${index}]`, String(item));
        }
      });
      return;
    }

    if (typeof value === 'object') {
      const nested = toFormUrlEncoded(value, formKey);
      nested.forEach((v, k) => params.append(k, v));
      return;
    }

    params.append(formKey, String(value));
  });

  return params;
}

export const accountSetupApi = createApi({
  reducerPath: 'accountApi',
  baseQuery: waPlatformQuery,
  tagTypes: ['Account'],
  endpoints: (builder) => ({
    confirmAccountJson: builder.query<
      ConfirmAccountJsonResponse,
      { token: string; username: string }
    >({
      query: ({ token, username }) => ({
        url: '/Account/ConfirmJson',
        method: 'GET',
        params: { token, username },
        headers: {
          Accept: 'application/json',
        },
      }),
      providesTags: ['Account'],
    }),
    confirmAccount: builder.mutation<ApiResponse, ConfirmAccountRequest>({
      query: (data) => ({
        url: '/Account/Confirm',
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json',
        },
        body: toFormUrlEncoded({
          model: data,
        }),
      }),
      transformResponse: normalizeApiResponse,
      invalidatesTags: ['Account'],
    }),
    saveAdvisorControlOnboarding: builder.mutation<
      ApiResponse,
      SaveAdvisorControlOnboardingRequest
    >({
      query: (body) => ({
        url: '/Account/SaveAdvisorControlOnboarding',
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json',
        },
        body: toFormUrlEncoded(body),
      }),
      transformResponse: normalizeApiResponse,
      invalidatesTags: ['Account'],
    }),
    saveTerms: builder.mutation<SaveTermsResponse, SaveTermsRequest>({
      query: (body) => ({
        url: '/Account/SaveTerms',
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json',
        },
        body: toFormUrlEncoded(body),
      }),
      transformResponse: normalizeApiResponse,
    }),
    setDefaultLanding: builder.mutation<
      SetDefaultLandingResponse,
      SetDefaultLandingRequest
    >({
      query: (body) => ({
        url: '/Account/SetDefaultLanding',
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json',
        },
        body: toFormUrlEncoded(body),
      }),
      transformResponse: normalizeApiResponse,
    }),
  }),
});

export const {
  useConfirmAccountMutation,
  useConfirmAccountJsonQuery,
  useSaveAdvisorControlOnboardingMutation,
  useSaveTermsMutation,
  useSetDefaultLandingMutation,
} = accountSetupApi;
